import api from './client';
import {
  Machine,
  MachineType,
  MachineNotes,
  MachineDocumentation,
  DocumentationListItem,
  MachinePhoto,
  MachineMaintenanceAction,
  Customer,
  CustomerDepartment,
  Address,
  PageResponse,
} from '../types';

export const machineApi = {
  getAll: () => api.get<Machine[]>('/api/machines'),
  getById: (id: string) => api.get<Machine>(`/api/machines/${id}`),
  getByQrSlug: (qrSlug: string) => api.get<Machine>(`/api/machines/by-qr/${qrSlug}`),
  create: (data: Partial<Machine>) => api.post<Machine>('/api/machines', data),
  update: (id: string, data: Partial<Machine>) => api.put<Machine>(`/api/machines/${id}`, data),
  delete: (id: string) => api.delete(`/api/machines/${id}`),
  getByType: (typeId: string) => api.get<Machine[]>(`/api/machines/by-type/${typeId}`),
  getByNipAndDepartment: (nip: string, departmentId: string) =>
    api.get<Machine[]>(`/api/machines/by-nip-and-department`, { params: { nip, departmentId } }),
  getByNip: (nip: string) => api.get<Machine[]>(`/api/machines/by-nip`, { params: { nip } }),
  setThumbnail: (machineId: string, photoId: string) =>
    api.put<Machine>(`/api/machines/${machineId}/thumbnail/${photoId}`),
  /** Zaproszenie klienta do maszyny: brak konta → mail z zaproszeniem, konto → mail informacyjny. */
  inviteUser: (machineId: string, email: string) =>
    api.post<{ status: string; message: string }>(`/api/machines/${machineId}/invite-user`, { email }),
  /** Włącza/wyłącza system zgłoszeń maszyny (admin integratora). 409 gdy są otwarte zgłoszenia. */
  setTicketSystem: (machineId: string, enabled: boolean) =>
    api.patch<Machine>(`/api/machines/${machineId}/ticket-system`, { enabled }),
  clearThumbnail: (machineId: string) =>
    api.delete<Machine>(`/api/machines/${machineId}/thumbnail`),
};

export const machineTypeApi = {
  getAll: () => api.get<MachineType[]>('/api/machine-types'),
  create: (data: Partial<MachineType>) => api.post<MachineType>('/api/machine-types', data),
  update: (id: string, data: Partial<MachineType>) => api.put<MachineType>(`/api/machine-types/${id}`, data),
  delete: (id: string) => api.delete(`/api/machine-types/${id}`),
};

export const notesApi = {
  getByMachineId: (machineId: string, page = 0, size = 5) =>
    api.get<PageResponse<MachineNotes>>(`/api/machines/${machineId}/notes`, { params: { page, size } }),
  create: (machineId: string, data: Partial<MachineNotes>) =>
    api.post<MachineNotes>(`/api/machines/${machineId}/notes`, data),
  delete: (id: string) => api.delete(`/api/machine-notes/${id}`),
};

/** Payload 409 z uploadu — identyczny plik już istnieje w bazie. */
export interface DuplicateDocumentationInfo {
  message: string;
  documentationId: string;
  name: string;
  description: string | null;
  alreadyLinkedToThisMachine: boolean;
  machines: { id: string; name: string }[];
}

export const documentationApi = {
  getByMachineId: (machineId: string) =>
    api.get<MachineDocumentation[]>(`/api/machines/${machineId}/documentation`),
  listAll: (params?: { nip?: string; machineId?: string; page?: number; size?: number }) =>
    api.get<PageResponse<DocumentationListItem>>('/api/documentation', { params }),
  upload: (machineId: string, file: File, name: string, description: string, createdByUserId: string) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', name);
    formData.append('description', description);
    formData.append('createdByUserId', createdByUserId);
    return api.post<MachineDocumentation>(`/api/machines/${machineId}/documentation`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  link: (machineId: string, documentationId: string) =>
    api.post(`/api/machines/${machineId}/documentation/${documentationId}/link`),
  unlink: (machineId: string, documentationId: string) =>
    api.delete(`/api/machines/${machineId}/documentation/${documentationId}/link`),
  delete: (id: string) => api.delete(`/api/machine-documentation/${id}`),
};

export const photoApi = {
  getByMachineId: (machineId: string) => api.get<MachinePhoto[]>(`/api/machines/${machineId}/photos`),
  upload: (machineId: string, file: File, description: string, createdByUserId: string) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('description', description);
    formData.append('createdByUserId', createdByUserId);
    return api.post<MachinePhoto>(`/api/machines/${machineId}/photos`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  delete: (id: string) => api.delete(`/api/machine-photos/${id}`),
};

export const maintenanceApi = {
  getByMachineId: (machineId: string, page = 0, size = 5) =>
    api.get<PageResponse<MachineMaintenanceAction>>(`/api/machines/${machineId}/maintenance`, { params: { page, size } }),
  create: (machineId: string, data: Partial<MachineMaintenanceAction>) =>
    api.post<MachineMaintenanceAction>(`/api/machines/${machineId}/maintenance`, data),
  delete: (id: string) => api.delete(`/api/machine-maintenance/${id}`),
};

export const customerApi = {
  // mine=true → only customers the caller's integrator services (has machines for).
  getAll: (mine?: boolean) =>
    api.get<Customer[]>('/api/customers', mine ? { params: { mine: true } } : undefined),
  getById: (id: string) => api.get<Customer>(`/api/customers/${id}`),
  getByNip: (nip: string) => api.get<Customer>(`/api/customers/by-nip/${nip}`),
  getMe: () => api.get<Customer>('/api/customers/me'),
  create: (data: Partial<Customer>) => api.post<Customer>('/api/customers', data),
  update: (id: string, data: Partial<Customer>) => api.put<Customer>(`/api/customers/${id}`, data),
  unlink: (id: string) => api.delete(`/api/customers/${id}/link`),
  uploadLogo: (id: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post<Customer>(`/api/customers/${id}/logo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteLogo: (id: string) => api.delete<Customer>(`/api/customers/${id}/logo`),
  uploadOwnLogo: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post<Customer>('/api/customers/me/logo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteOwnLogo: () => api.delete<Customer>('/api/customers/me/logo'),
  getDepartments: (customerId: string) =>
    api.get<CustomerDepartment[]>(`/api/customers/${customerId}/departments`),
  createDepartment: (customerId: string, data: Partial<CustomerDepartment>) =>
    api.post<CustomerDepartment>(`/api/customers/${customerId}/departments`, data),
};

export const addressApi = {
  getById: (id: string) => api.get<Address>(`/api/addresses/${id}`),
  create: (data: Partial<Address>) => api.post<Address>('/api/addresses', data),
  update: (id: string, data: Partial<Address>) => api.put<Address>(`/api/addresses/${id}`, data),
};
